import { sumReducer } from '@/utils/functionalBob'
import _groupBy from 'lodash/groupBy'
import _sumBy from 'lodash/sumBy'
import _flatMap from 'lodash/flatMap'
import _differenceBy from 'lodash/differenceBy'

export const buildProductIdentity = (product) => {
  const id = product && product.id
  const lotNumber = product && product.lotInformation && product.lotInformation.lotNumber
  const expirationDate = product && product.lotInformation && product.lotInformation.expirationDate
  return `${id}-${lotNumber}-${expirationDate}`
}

export function validateBoxDimensions (boxConfig) {
  return boxConfig.length > 0 && boxConfig.width > 0 && boxConfig.height > 0 &&
  boxConfig.length.toString().length <= 3 && boxConfig.width.toString().length <= 3 &&
  boxConfig.height.toString().length <= 3
}

export function validateBoxWeight (boxConfig) {
  return (boxConfig.weight.pounds > 0 || boxConfig.weight.ounces > 0) && // At least one of the fields needs to be greater than 0
  !(boxConfig.weight.pounds < 0 || boxConfig.weight.ounces < 0) && // Both fields cannot be negative
  (boxConfig.weight.pounds.toString().length <= 6 && boxConfig.weight.ounces.toString().length <= 6) // Both fields can only have <= 6 digits
}

// single sku
export function isNonEmpty (shippingConfigurations) {
  return shippingConfigurations.every(areAllValuesNonEmpty)
}
export function isEmpty (shippingConfigurations) {
  return shippingConfigurations.every(areAllValuesEmpty)
}
export function areAllValuesEmpty (sc) {
  return sc.boxConfigurations.every(bc => bc.quantityPerBox === 0 && bc.numberOfBoxes === 0)
}
export function areAllValuesNonEmpty (sc) {
  return !sc.boxConfigurations.find(bc => bc.quantityPerBox === 0 || bc.numberOfBoxes === 0)
}
export function totalShipmentQuantities (shippingConfigurations) {
  return shippingConfigurations.map(sc => sc.shipmentQuantity).reduce(sumReducer, 0)
}

export function computedTotalQuantity (shippingConfig) {
  return shippingConfig.boxConfigurations.reduce((acc, bc) => (bc.quantityPerBox * bc.numberOfBoxes) + acc, 0)
}

export function totalBoxConfigurationQuantities (shippingConfigurations) {
  return shippingConfigurations.map(computedTotalQuantity)
}

export function validateTotalQuantity (shippingConfig, shipmentQuantity) {
  return computedTotalQuantity(shippingConfig) === shipmentQuantity && areAllValuesNonEmpty(shippingConfig)
}

export function validateShipmentQuantities (shippingConfigurations) {
  return totalBoxConfigurationQuantities(shippingConfigurations).reduce(sumReducer, 0) === totalShipmentQuantities(shippingConfigurations)
}

export function validateSingleSkuShippingConfigurations (shippingConfigurations) {
  return _flatMap(shippingConfigurations.map(sc => sc.boxConfigurations.map(bc => validateBoxDimensions(bc) && validateBoxWeight(bc)))).every(val => !!val)
}

export function isValidSingleSkuHelper (shippingConfigurations, includeDimensions) {
  let valid = false

  const validations = [
    validateShipmentQuantities,
    ...(includeDimensions ? [ validateSingleSkuShippingConfigurations ] : [])
  ]

  for (let i = 0; i < validations.length; i++) {
    valid = validations[i](shippingConfigurations)
    if (!valid) break
  }

  return valid
}

// multi sku
export function extractShipmentQuantityHelper (_selectedProducts, product) {
  const notFound = -1
  if (!product) return notFound
  const quantityToSend = !product.isLotProduct ? product.quantitiesToSend[0]
          : product.quantitiesToSend.find(qts => qts.lotNumber === product.lotInformation.lotNumber && qts.expirationDate === product.lotInformation.expirationDate)
  return quantityToSend ? quantityToSend.amount : notFound
}

export function extractTotalQuantityPerConfigurationHelper (configuration) {
  return configuration.quantityPerBox * configuration.numberOfBoxes
}

export function extractQuantityRemainingHelper (selectedProducts, boxConfigurations, configuration) {
  const notFound = -1
  if (!configuration.product) return notFound
  const shipmentQuantity = extractShipmentQuantityHelper(selectedProducts, configuration.product)
  if (shipmentQuantity < 0) return notFound
  const groupedProducts = _groupBy(_flatMap(boxConfigurations.map(bc => bc.skuConfigurations)), (config) => {
    return buildProductIdentity(config.product)
  })

  const groupingId = buildProductIdentity(configuration.product)
  const grouping = groupedProducts[groupingId]
  const totalQuantity = _sumBy(grouping, extractTotalQuantityPerConfigurationHelper)
  return shipmentQuantity - totalQuantity
}

export function validateSelectedProductsHelper ({ selectedProducts, boxConfigurations }) {
  const productsSelected = _flatMap(boxConfigurations.map(bc => bc.skuConfigurations.map(sc => sc.product)))
  if (productsSelected.some(p => p === null)) {
    return false
  } else {
    const productsRemaining = _differenceBy(selectedProducts, productsSelected, p => buildProductIdentity(p))
    return productsRemaining.length === 0
  }
}

export function extractQuantitiesRemainingPerProduct ({ selectedProducts, boxConfigurations }) {
  return _flatMap(boxConfigurations.map(bc => bc.skuConfigurations.map(sc => ({
    remaining: extractQuantityRemainingHelper(selectedProducts, boxConfigurations, sc),
    product: sc.product
  }))))
}

export function validateShipmentQuantityHelper ({ selectedProducts, boxConfigurations }) {
  const quantitiesRemaining = extractQuantitiesRemainingPerProduct({ selectedProducts, boxConfigurations })
  return !quantitiesRemaining.some(qr => qr.remaining !== 0)
}

export function validateQuantityInputs ({ boxConfigurations }) {
  return _flatMap(boxConfigurations.map(bc => bc.skuConfigurations.map(sc => sc.numberOfBoxes > 0 && sc.quantityPerBox > 0))).every(val => !!val)
}

export function validateMultiSkuBoxDimensionsAndWeight (boxConfigurations) {
  return validateBoxDimensions(boxConfigurations) && validateBoxWeight(boxConfigurations)
}

export function validateMultiSkuShippingConfigurations ({ boxConfigurations }) {
  return _flatMap(boxConfigurations.map(bc => validateMultiSkuBoxDimensionsAndWeight(bc.skuConfigurations[0]))).every(val => !!val)
}

export function isValidMultiSkuHelper ({ selectedProducts, boxConfigurations }, includeDimensions) {
  let valid = false
  const validations = [
    validateSelectedProductsHelper,
    validateShipmentQuantityHelper,
    validateQuantityInputs,
    ...(includeDimensions ? [ validateMultiSkuShippingConfigurations ] : [])
  ]

  for (let i = 0; i < validations.length; i++) {
    valid = validations[i]({ selectedProducts, boxConfigurations })
    if (!valid) break
  }

  return valid
}
